const PROVIDERS = [
  { id: "google_meet", label: "Google Meet", host: /(^|\.)meet\.google\.com$/i },
  { id: "xpro_video", label: "Vídeo XPro", host: null, path: /^\/video\//i },
  { id: "teams", label: "Microsoft Teams", host: /(^|\.)teams\.microsoft\.com$/i },
  { id: "zoom", label: "Zoom", host: /(^|\.)zoom\.us$/i },
  { id: "webex", label: "Webex", host: /(^|\.)webex\.com$/i },
  { id: "whereby", label: "Whereby", host: /(^|\.)whereby\.com$/i },
  { id: "gotomeeting", label: "GoTo Meeting", host: /(^|\.)gotomeeting\.com$/i },
];

const URL_IN_TEXT = /https?:\/\/[^\s<>"')\]]+/gi;
const HREF_IN_HTML = /href=["']([^"']+)["']/gi;

const MAX_PRIMARY_LINKS = 2;

function trimUrlTrailingPunctuation(url) {
  return String(url || "").replace(/[.,;:!?)>\]]+$/g, "");
}

function detectProvider(url) {
  let host = "";
  let path = "";
  try {
    const parsed = new URL(url);
    host = parsed.hostname.toLowerCase();
    path = parsed.pathname || "";
  } catch {
    return null;
  }
  for (const p of PROVIDERS) {
    if (p.id === "xpro_video") {
      if (p.path?.test(path)) return { id: p.id, label: p.label };
      continue;
    }
    if (p.host?.test(host)) return { id: p.id, label: p.label };
  }
  return null;
}

function isTeamsLauncher(url) {
  return /\/dl\/launcher\//i.test(url) || /launcher\.html/i.test(url);
}

function isZoomJoinPath(pathname) {
  return /\/j\/\d+/i.test(pathname) || /\/my\/\w+\/join/i.test(pathname);
}

function isWebexJoinPath(pathname) {
  return /\/(meet|join)\//i.test(pathname);
}

/** Links que abrem a sala de videoconferência — exibidos como botão principal. */
export function isPrimaryJoinLink(url, providerId) {
  if (!url || !providerId) return false;

  try {
    const u = new URL(url);
    const path = u.pathname.toLowerCase();

    if (providerId === "google_meet") return true;
    if (providerId === "xpro_video") return /\/video\//i.test(url);

    if (providerId === "teams") {
      if (isTeamsLauncher(url)) return false;
      return path.includes("/meetup-join/") || path.includes("/l/meet/") || path.includes("/meet/");
    }

    if (providerId === "zoom") return isZoomJoinPath(path);

    if (providerId === "webex") return isWebexJoinPath(path);

    if (providerId === "whereby" || providerId === "gotomeeting") return true;

    return false;
  } catch {
    return false;
  }
}

function joinLinkScore(url, providerId) {
  try {
    const u = new URL(url);
    const path = u.pathname.toLowerCase();

    if (providerId === "teams") {
      if (isTeamsLauncher(url)) return 0;
      if (path.includes("/l/meet/")) return 100;
      if (path.includes("/meetup-join/")) return 90;
      if (path.includes("/meet/")) return 80;
      return 5;
    }

    if (providerId === "google_meet") {
      if (path.includes("/lookup/")) return 40;
      return 100;
    }

    if (providerId === "xpro_video") return 100;

    if (providerId === "zoom") {
      if (isZoomJoinPath(path)) return 100;
      return 10;
    }

    if (providerId === "webex") {
      if (isWebexJoinPath(path)) return 100;
      return 10;
    }

    return 50;
  } catch {
    return 0;
  }
}

function teamsMeetingKey(url) {
  try {
    const u = new URL(url);
    const launcherTarget = u.searchParams.get("url");
    if (launcherTarget && isTeamsLauncher(url)) {
      return teamsMeetingKey(new URL(launcherTarget, "https://teams.microsoft.com").toString());
    }

    const path = decodeURIComponent(u.pathname);
    const meetup = path.match(/meetup-join\/([^/]+)/i);
    if (meetup) return `teams:${meetup[1]}`;

    const meet = path.match(/\/meet\/([^/?]+)/i);
    if (meet) return `teams:${meet[1]}`;

    return `teams:${path.split("?")[0]}`;
  } catch {
    return `teams:${url}`;
  }
}

function meetingLinkKey(url, providerId) {
  try {
    const u = new URL(url);

    if (providerId === "teams") return teamsMeetingKey(url);

    if (providerId === "google_meet") {
      const code = u.pathname.replace(/^\//, "").split("/")[0];
      return code ? `google_meet:${code}` : `google_meet:${u.pathname}`;
    }

    if (providerId === "zoom") {
      const m = u.pathname.match(/\/j\/(\d+)/i);
      if (m) return `zoom:${m[1]}`;
    }

    return `${providerId}:${u.origin}${u.pathname}`;
  } catch {
    return `${providerId}:${url}`;
  }
}

function addCandidate(map, rawUrl, forcedProvider) {
  const url = trimUrlTrailingPunctuation(rawUrl);
  if (!url || !/^https?:\/\//i.test(url)) return;

  const provider = forcedProvider || detectProvider(url);
  if (!provider) return;

  const entry = {
    url,
    provider: provider.id,
    label: provider.label,
    score: joinLinkScore(url, provider.id),
    primary: isPrimaryJoinLink(url, provider.id),
  };

  const key = meetingLinkKey(url, provider.id);
  const existing = map.get(key);
  if (!existing || entry.score > existing.score) {
    map.set(key, entry);
  }
}

function extractUrlsFromText(text) {
  if (!text) return [];
  const urls = [];
  const raw = String(text);
  const plain = raw.match(URL_IN_TEXT) || [];
  urls.push(...plain);

  let m;
  const hrefRe = new RegExp(HREF_IN_HTML.source, HREF_IN_HTML.flags);
  while ((m = hrefRe.exec(raw)) !== null) {
    urls.push(m[1]);
  }
  return urls;
}

function collectCandidates({ description = null, location = null, meetingLinks = null } = {}) {
  const map = new Map();

  if (Array.isArray(meetingLinks)) {
    for (const link of meetingLinks) {
      if (!link?.url) continue;
      const provider = link.provider
        ? PROVIDERS.find((p) => p.id === link.provider) || { id: link.provider, label: link.label }
        : detectProvider(link.url);
      if (provider) {
        addCandidate(map, link.url, { id: provider.id, label: provider.label || link.label });
      }
    }
  }

  for (const url of extractUrlsFromText(location)) addCandidate(map, url);
  for (const url of extractUrlsFromText(description)) addCandidate(map, url);

  return Array.from(map.values());
}

/**
 * Retorna apenas links de entrada na reunião (deduplicados, no máximo 2).
 */
export function extractAgendaMeetingLinks({
  description = null,
  location = null,
  meetingLinks = null,
} = {}) {
  const candidates = collectCandidates({ description, location, meetingLinks });
  const primary = candidates
    .filter((c) => c.primary && c.score > 0)
    .sort((a, b) => b.score - a.score);

  const byProvider = new Map();
  for (const link of primary) {
    if (!byProvider.has(link.provider)) {
      byProvider.set(link.provider, link);
    }
  }

  return Array.from(byProvider.values())
    .sort((a, b) => b.score - a.score)
    .slice(0, MAX_PRIMARY_LINKS)
    .map(({ url, provider, label }) => ({ url, provider, label }));
}

export function getPrimaryMeetingUrls(options) {
  return new Set(extractAgendaMeetingLinks(options).map((l) => l.url));
}

export function htmlToPlainDescription(text) {
  if (!text) return "";
  return String(text)
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/p>/gi, "\n")
    .replace(/<\/div>/gi, "\n")
    .replace(/<li[^>]*>/gi, "\n• ")
    .replace(/<[^>]+>/g, "")
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .replace(/&quot;/gi, '"')
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

function normalizeUrlForCompare(url) {
  return trimUrlTrailingPunctuation(url).replace(/\/$/, "").toLowerCase();
}

export function isUrlShownAsMeetingLink(url, primaryUrls) {
  const norm = normalizeUrlForCompare(url);
  for (const primary of primaryUrls) {
    if (normalizeUrlForCompare(primary) === norm) return true;
  }
  return false;
}

/**
 * Quebra texto em segmentos de texto puro e links clicáveis (exceto os já exibidos como botão).
 */
export function linkifyDescriptionSegments(text, primaryUrls = new Set()) {
  const plain = htmlToPlainDescription(text);
  if (!plain) return [];

  const segments = [];
  const re = new RegExp(URL_IN_TEXT.source, "gi");
  let lastIndex = 0;
  let match;

  while ((match = re.exec(plain)) !== null) {
    const url = trimUrlTrailingPunctuation(match[0]);
    if (match.index > lastIndex) {
      const chunk = plain.slice(lastIndex, match.index);
      if (chunk) segments.push({ type: "text", value: chunk });
    }

    if (!isUrlShownAsMeetingLink(url, primaryUrls)) {
      segments.push({ type: "link", value: url, href: url });
    }

    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < plain.length) {
    const tail = plain.slice(lastIndex);
    if (tail.trim()) segments.push({ type: "text", value: tail });
  }

  return segments;
}

export function meetingProviderLabel(provider, t) {
  const key = `meetingProviders.${provider}`;
  const translated = t?.(key);
  if (translated && translated !== key) return translated;
  const fallback = PROVIDERS.find((p) => p.id === provider);
  return fallback?.label || t?.("meetingProviders.other") || "Videoconferência";
}
